import { DeleteOutlined, RedoOutlined, UndoOutlined } from "@ant-design/icons";
import { AddToPhotos } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import {
  Button,
  Col,
  Dropdown,
  Popover,
  Progress,
  Row,
  Slider,
  Tooltip
} from "antd";
import MultiFileUploader from "components/UI/admin/MultifileUploadGallery";
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import DefaultAudioGenerator from "components/UI/mediaPlayers/defaultAudioGenerator";
import React, { useEffect, useRef, useState } from "react";
import { SketchPicker } from "react-color";
import { FileUploader } from "react-drag-drop-files";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import COLOR from "utils/constants/color";
import { imageUploader } from "utils/methods/imageUploader";
import { disableBodyScroll } from "utils/methods/math";
import { IMAGE_FILE_TYPE, UPLOAD_IMAGE_TO_FIREBASE } from "utils/constants/enum"
const CanvasContainer = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  cursor: ${({ isPointer }) => (isPointer ? "pointer" : "default")};
`;

const Image = styled.img`
  background-color: ${COLOR.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
  height: 100%;
  width: 100%;
  object-fit:contain;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  margin-bottom: 30px;
`;

const ControlsContainer = styled.div`
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
  flex-wrap: wrap;
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.9);
  @media print {
    display: none;
  }
`;

const LineWidthPreview = styled.div`
  width: 100px;
  height: ${({ strokeWidth }) => strokeWidth}px;
  background-color: ${({ color }) => (color ? color : "black")};
  margin-top: ${({ top }) => top}px;
`;
const FileUploadArea = styled.div`
  height: 100%;
  border: 1px dashed gray;
  padding: 5px 12px;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Column = styled(Col)`
    display: flex;
    justify-content: center;
    align-items: center;
}
`;
const Text = styled.div`
  font-size: 12px;
`;

const SlideHighLighter = ({
  imageUrl,
  pageId,
  bookId,
  setImageConfiguration,
  onLoading,
  onChange,
  setpagePoster,
  isNonNative = false,
  handleSaveRectangle,
  rectangles,
  setRectangles,
  activeSlide,
  getBookSlides,
}) => {
  const showAlert = useAlert();
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [newRect, setNewRect] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#50e3c2");
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [opacity, setOpacity] = useState(1);
  const [naturalDimensions, setNaturalDimensions] = useState({
    width: 1,
    height: 1,
  });
  const [isHighlighterMode, setIsHighlighterMode] = useState(false);
  const [isAnnotationsVisible, setIsAnnotationsVisible] = useState(true);
  const [history, setHistory] = useState([]);
  const [redoHistory, setRedoHistory] = useState([]);
  const [hoveredRectIndex, setHoveredRectIndex] = useState(null);
  const [highlights, setHighlights] = useState({});
  const [is_image_loaded, setIsImageLoaded] = useState(false);
  const [loadingMap, setLoadingMap] = useState(0);
  const [loader, setLoader] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [rectangleIndex, setRectangleIndex] = useState({});
  const [videoUrl, setVideoUrl] = useState("");
  const [openAllModal, setAllModal] = useState(false);
  const [openAudioModal, setAudioModal] = useState(false);
  const [openVideoModal, setVideoModal] = useState(false);
  const [openDefinitionModal, setDefinitionModal] = useState(false);
  const [resultRectangle, setResultRectange] = useState("");
  const [isPointer, setIsPointer] = useState(false);

  useEffect(() => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    if (canvas && image) {
      setNaturalDimensions({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    }
  }, [imageRef, canvasRef, activeSlide, getBookSlides]);

  const normalizeRectangles = (rects) => {
    if (!Array.isArray(rects)) return [];
    return rects.map(rect => ({
      ...rect,
      isHighlighterMode: false
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!imageUrl && imageUrl === "") {
          return;
        }

        if (bookId && pageId && getBookSlides) {
          if (getBookSlides) {
            if (
              highlights &&
              highlights.page_id === getBookSlides.page_id &&
              highlights.book_id === getBookSlides.book_id
            ) {
              return;
            }
            setHighlights(getBookSlides);
            if (getBookSlides?.pages[activeSlide]?.data && Array.isArray(getBookSlides?.pages[activeSlide]?.data)) {
              const updatedData = normalizeRectangles(getBookSlides?.pages[activeSlide]?.data);
              setRectangles(updatedData);
            } else {
              setRectangles([]);
              setHighlights({
                page_id: pageId,
                book_id: bookId,
                pages: [],
              });
            }
          } else {
            setRectangles([]);
            setHighlights({
              page_id: pageId,
              book_id: bookId,
              pages: [],
            });
            const canvas = canvasRef.current;
            const image = imageRef.current;
            if (canvas && image) {
              setNaturalDimensions({
                width: image.naturalWidth,
                height: image.naturalHeight,
              });
            }
          }
        }
      } catch (error) {
        Sentry.captureException(`615 Error in fetchData: ${error}`);
        console.log(error);
      }
    };

    fetchData();
  }, [pageId, bookId, getBookSlides, activeSlide]);

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const image = imageRef.current;
      if (canvas && image) {
        canvas.width = image.clientWidth;
        canvas.height = image.clientHeight;
        setImageConfiguration({
          width: image.naturalWidth,
          height: image.naturalHeight,
          clientWidth: image.clientWidth,
          clientHeight: image.clientHeight,
        });
        redrawCanvas();
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [rectangles, isAnnotationsVisible]);

  const getRelativePosition = (event) => {
    try {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        return {
          x:
            ((event?.clientX - rect?.left) / rect?.width) *
            naturalDimensions?.width,
          y:
            ((event?.clientY - rect?.top) / rect?.height) *
            naturalDimensions?.height,
        };
      }
    } catch (error) {
      Sentry.captureException(`616 Error in getRelativePosition: ${error}`);
      console.log(error);
    }
  };

  const getTouchRelativePosition = (touch) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((touch.clientX - rect.left) / rect.width) * naturalDimensions.width,
        y:
          ((touch.clientY - rect.top) / rect.height) * naturalDimensions.height,
      };
    }
  };

  const disableScroll = () => {
    if (window.innerWidth < 800) {
      disableBodyScroll('SlideHighlighter');
    }
  };

  const enableScroll = () => {
    if (window.innerWidth < 800) {
      document.body.style.overflow = "auto";
    }
  };

  const checkOverlap = (rect1, existingRectangles) => {
    const MARGIN = 10;

    // For a new rectangle being drawn
    // First normalize the coordinates for the new rectangle
    const normalizedRect1 = {
      x: rect1.width < 0 ? rect1.x + rect1.width : rect1.x,
      y: rect1.height < 0 ? rect1.y + rect1.height : rect1.y,
      width: Math.abs(rect1.width),
      height: Math.abs(rect1.height),
    };

    // Now create boundaries with margin
    const r1 = {
      left: normalizedRect1.x - MARGIN,
      right: normalizedRect1.x + normalizedRect1.width + MARGIN,
      top: normalizedRect1.y - MARGIN,
      bottom: normalizedRect1.y + normalizedRect1.height + MARGIN,
    };

    return existingRectangles.some((rect2) => {
      // Normalize coordinates for existing rectangle
      const normalizedRect2 = {
        x: rect2.width < 0 ? rect2.x + rect2.width : rect2.x,
        y: rect2.height < 0 ? rect2.y + rect2.height : rect2.y,
        width: Math.abs(rect2.width),
        height: Math.abs(rect2.height),
      };

      // Create boundaries with margin for existing rectangle
      const r2 = {
        left: normalizedRect2.x - MARGIN,
        right: normalizedRect2.x + normalizedRect2.width + MARGIN,
        top: normalizedRect2.y - MARGIN,
        bottom: normalizedRect2.y + normalizedRect2.height + MARGIN,
      };

      // Check if rectangles overlap with margin
      return !(
        r2.left > r1.right ||
        r2.right < r1.left ||
        r2.top > r1.bottom ||
        r2.bottom < r1.top
      );
    });
  };

  const handleMouseDown = (e) => {
    if (hoveredRectIndex !== null) return;
    setIsDrawing(true);
    disableScroll();
    const { x, y } = getRelativePosition(e);
    setNewRect({
      x,
      y,
      width: 0,
      height: 0,
      color: selectedColor,
      strokeWidth,
      isHighlighterMode: false,
      opacity,
      non_native: isNonNative ? true : false,
      naturalDimensions,
    });
  };

  const handleMouseMove = (e) => {
    const { x, y } = getRelativePosition(e);
    if (isDrawing) {
      const updatedRect = {
        ...newRect,
        width: x - newRect.x,
        height: y - newRect.y,
      };
      if (!checkOverlap(updatedRect, rectangles)) {
        setNewRect((prevRect) => ({
          ...prevRect,
          width: x - prevRect.x,
          height: y - prevRect.y,
        }));
      }
    } else {
      // Updated hover detection with normalized coordinates
      const hoveredIndex = rectangles.findIndex((rect) => {
        // Normalize rectangle coordinates
        const normalizedX = Math.min(rect.x, rect.x + rect.width);
        const normalizedY = Math.min(rect.y, rect.y + rect.height);
        const normalizedWidth = Math.abs(rect.width);
        const normalizedHeight = Math.abs(rect.height);

        return (
          x >= normalizedX &&
          x <= normalizedX + normalizedWidth &&
          y >= normalizedY &&
          y <= normalizedY + normalizedHeight
        );
      });

      setIsPointer(hoveredIndex !== -1);
      setHoveredRectIndex(hoveredIndex !== -1 ? hoveredIndex : null);
    }
  };

  const handleMouseUp = () => {
    if (isDrawing) {
      enableScroll();
      if (newRect.width !== 0 && newRect.height !== 0) {
        const updatedRectangles = [...rectangles, newRect];
        setHistory([...history, rectangles]);
        setRectangles(updatedRectangles);
        saveRectanglesToDB(updatedRectangles);
      }
      setNewRect(null);
      setIsDrawing(false);
      setRedoHistory([]); // Clear redo history on new draw action
    }
  };

  const handleTouchStart = (e) => {
    if (hoveredRectIndex !== null) return;
    setIsDrawing(true);
    disableScroll();
    const { x, y } = getTouchRelativePosition(e.touches[0]);
    setNewRect({
      x,
      y,
      width: 0,
      height: 0,
      color: selectedColor,
      strokeWidth,
      isHighlighterMode: false,
      opacity,
      non_native: isNonNative ? true : false,
      naturalDimensions,
    });
  };

  const handleTouchMove = (e) => {
    const { x, y } = getTouchRelativePosition(e.touches[0]);
    if (isDrawing && newRect) {
      const updatedRect = {
        ...newRect,
        width: x - newRect.x,
        height: y - newRect.y,
      };

      if (!checkOverlap(updatedRect, rectangles)) {
        setNewRect((prevRect) => ({
          ...prevRect,
          width: x - prevRect.x,
          height: y - prevRect.y,
        }));
      }
    } else {
      const hoveredIndex = rectangles.findIndex(
        (rect) =>
          x >= rect.x &&
          x <= rect.x + rect.width &&
          y >= rect.y &&
          y <= rect.y + rect.height
      );
      setHoveredRectIndex(hoveredIndex !== -1 ? hoveredIndex : null);
    }
  };

  const handleTouchEnd = () => {
    if (isDrawing) {
      enableScroll();
      if (newRect.width !== 0 && newRect.height !== 0) {
        const updatedRectangles = [...rectangles, newRect];
        setHistory([...history, rectangles]);
        setRectangles(updatedRectangles);
        saveRectanglesToDB(updatedRectangles);
      }
      setNewRect(null);
      setIsDrawing(false);
      setRedoHistory([]); // Clear redo history on new draw action
    }
  };

  const handleColorChange = (color) => {
    setSelectedColor(color.hex);
    localStorage.setItem("selectedColor", color.hex);
  };

  const handleStrokeWidthChange = ({ key }) => {
    setStrokeWidth(Number(key));
    localStorage.setItem("strokeWidth", key);
  };

  const handleOpacityChange = (value) => {
    setOpacity(value / 100);
    localStorage.setItem("opacity", value / 100);
  };

  const handleModeChange = (checked) => {
    setIsHighlighterMode(checked);
    localStorage.setItem("isHighlighterMode", checked);
  };

  const handleUndo = () => {
    if (history.length > 0) {
      const previousState = history[history.length - 1];
      const normalizedState = normalizeRectangles(previousState);
      setRedoHistory([rectangles, ...redoHistory]);
      setRectangles(normalizedState);
      saveRectanglesToDB(normalizedState);
      setHistory(history.slice(0, -1));
    }
  };

  const handleRedo = () => {
    if (redoHistory.length > 0) {
      const nextState = redoHistory[0];
      const normalizedState = normalizeRectangles(nextState);
      setHistory([...history, rectangles]);
      setRectangles(normalizedState);
      saveRectanglesToDB(normalizedState);
      setRedoHistory(redoHistory.slice(1));
    }
  };

  const handleDelete = (index) => {
    const updatedRectangles = rectangles.filter((_, i) => i !== index);
    setHistory([...history, rectangles]);
    setRectangles(updatedRectangles);
    saveRectanglesToDB(updatedRectangles);
  };

  const handleClearAll = () => {
    setHistory([...history, rectangles]);
    setRectangles([]);
    saveRectanglesToDB([]);
  };

  const saveRectanglesToDB = async (rectangles, show = true) => {
    try {
      const normalizedRectangles = normalizeRectangles(rectangles);
      handleSaveRectangle(normalizedRectangles);
      setNaturalDimensions(naturalDimensions);
    } catch (error) {
      Sentry.captureException(`617 Error in saveRectanglesToDB: ${error}`);
      showAlert("Failed to save", 'error');
    }
  };

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    try {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (rectangles?.length > 0) {
        rectangles.forEach((rect, index) => {
          // First draw the highlight rectangle
          ctx.strokeStyle = rect.color;
            ctx.lineWidth = rect.strokeWidth;
            ctx.strokeRect(
              (rect.x / naturalDimensions.width) * canvas.width,
              (rect.y / naturalDimensions.height) * canvas.height,
              (rect.width / naturalDimensions.width) * canvas.width,
              (rect.height / naturalDimensions.height) * canvas.height
            );

          // Calculate the actual rectangle boundaries
          const rectX = (rect.x / naturalDimensions.width) * canvas.width;
          const rectY = (rect.y / naturalDimensions.height) * canvas.height;
          const rectWidth =
            (rect.width / naturalDimensions.width) * canvas.width;
          const rectHeight =
            (rect.height / naturalDimensions.height) * canvas.height;

          // Normalize coordinates for buttons
          const normalizedX = Math.min(rectX, rectX + rectWidth);
          const normalizedY = Math.min(rectY, rectY + rectHeight);
          const normalizedMaxX = Math.max(rectX, rectX + rectWidth);

          // Draw Upload button - positioned at top-left
          const uploadSize = 60;
          const uploadHeight = 25;
          ctx.fillStyle = "teal";
          ctx.fillRect(normalizedX, normalizedY, uploadSize, uploadHeight);
          ctx.fillStyle = "white";
          ctx.font = "14px Arial";
          ctx.fillText("Upload", normalizedX + 6, normalizedY + 17);

          // Store upload button coordinates
          rect.audioButton = {
            x: normalizedX,
            y: normalizedY,
            width: uploadSize,
            height: uploadHeight,
          };

          // Draw Close (X) button - positioned at top-right
          const crossSize = 20;
          const crossX = normalizedMaxX - crossSize; // Align to right edge
          ctx.fillStyle = "red";
          ctx.fillRect(crossX, normalizedY, crossSize, crossSize);
          ctx.fillStyle = "white";
          ctx.font = "14px Arial";
          ctx.fillText("X", crossX + 5, normalizedY + 15);

          // Store close button coordinates
          rect.crossButton = {
            x: crossX,
            y: normalizedY,
            width: crossSize,
            height: crossSize,
          };
        });
      }

      if (newRect) {
        ctx.strokeStyle = newRect.color;
        ctx.lineWidth = newRect.strokeWidth;
        ctx.strokeRect(
          (newRect.x / naturalDimensions.width) * canvas.width,
          (newRect.y / naturalDimensions.height) * canvas.height,
          (newRect.width / naturalDimensions.width) * canvas.width,
          (newRect.height / naturalDimensions.height) * canvas.height
        );
      }
    } catch (error) {
      Sentry.captureException("618 Error in redrawCanvas: ${error}");
      console.error("Error in redrawCanvas:", error);
    }
  };

  useEffect(() => {
    redrawCanvas();
  }, [rectangles, newRect, isAnnotationsVisible, hoveredRectIndex]);

  const strokeWidthMenu = {
    items: [2, 3, 4, 5, 6, 7, 8].map((width) => ({
      key: width,
      label: <LineWidthPreview strokeWidth={width} top={5} color="black" />,
    })),
    onClick: handleStrokeWidthChange,
  };

  const handleMouseLeave = () => {
    setHoveredRectIndex(null);
  };

  const handleChange = (file) => {
    setHistory([...history, rectangles]);
    setRectangles([]);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    saveRectanglesToDB([]);
    uploadToStorage(file, pageId);
    reader.onloadend = function (e) {
      let v = reader.result;
      setpagePoster(v);
    };
  };

  const callLoader = (val) => {
    setLoader(val);
    if (onLoading) {
      onLoading(val);
    }
  };
  const uploadToStorage = async (file, index) => {
    if (file) {
      try {
        const onUpdatePer = (per) => {
          setLoadingMap(per);
          callLoader(true);
        };
        const download_url = await imageUploader(file, onUpdatePer,UPLOAD_IMAGE_TO_FIREBASE);
        if (download_url) {
          onChange({ url: download_url });
        } else {
          showAlert("Unable to upload image.", 'error');
        }
      } catch (error) {
        console.error("Upload error:", error);
        Sentry.captureException(`619 Error in uploadToStorage: ${error}`);
        showAlert("Unable to upload image.", "error");
      } finally {
        callLoader(false);
        setLoadingMap(0);
      }
    } else {
      console.log("No file to upload.");
    }
  };
  const getEventCoordinates = (e) => {
    if (e.type === "touchstart" || e.type === "touchend") {
      const touch = e.changedTouches[0];
      return {
        clientX: touch.clientX,
        clientY: touch.clientY,
      };
    }
    return {
      clientX: e.clientX,
      clientY: e.clientY,
    };
  };

  const calculateCanvasCoordinates = (clientX, clientY, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;

    // Account for canvas scaling
    const scaleX = canvas.width / canvas.clientWidth;
    const scaleY = canvas.height / canvas.clientHeight;

    return {
      scaledX: mouseX * scaleX,
      scaledY: mouseY * scaleY,
      mouseX,
      mouseY,
    };
  };

  const isClickInside = (x, y, button) => {
    if (!button) return false;
    return (
      x >= button.x &&
      x <= button.x + button.width &&
      y >= button.y &&
      y <= button.y + button.height
    );
  };

  const handleCanvasClick = (e) => {
    e.preventDefault(); // Prevent any default behavior

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { clientX, clientY } = getEventCoordinates(e);
    const { scaledX, scaledY, mouseX, mouseY } = calculateCanvasCoordinates(
      clientX,
      clientY,
      canvas
    );

    for (let i = 0; i < rectangles.length; i++) {
      const rectangle = rectangles[i];

      // Check for close button click
      if (isClickInside(mouseX, mouseY, rectangle.crossButton)) {
        handleDelete(i);
        return;
      }

      // Check for upload button click
      if (isClickInside(mouseX, mouseY, rectangle.audioButton)) {
        setAllModal(true);
        setRectangleIndex(i);
        setAudioUrl(rectangle.audioFile || "");
        setVideoUrl(rectangle.videoFile || "");
        setResultRectange(rectangle);
        return;
      }

      // Calculate normalized rectangle coordinates
      const rectX = (rectangle.x / naturalDimensions.width) * canvas.width;
      const rectY = (rectangle.y / naturalDimensions.height) * canvas.height;
      const rectWidth =
        (rectangle.width / naturalDimensions.width) * canvas.width;
      const rectHeight =
        (rectangle.height / naturalDimensions.height) * canvas.height;

      // Normalize rectangle boundaries
      const normalizedX = Math.min(rectX, rectX + rectWidth);
      const normalizedY = Math.min(rectY, rectY + rectHeight);
      const normalizedWidth = Math.abs(rectWidth);
      const normalizedHeight = Math.abs(rectHeight);

      if (
        mouseX >= normalizedX &&
        mouseX <= normalizedX + normalizedWidth &&
        mouseY >= normalizedY &&
        mouseY <= normalizedY + normalizedHeight &&
        isPointer
      ) {
        const isButtonArea =
          isClickInside(mouseX, mouseY, rectangle.crossButton) ||
          isClickInside(mouseX, mouseY, rectangle.audioButton);

        if (!isButtonArea) {
          setAllModal(true);
          setRectangleIndex(i);
          setAudioUrl(rectangle.audioFile || "");
          setVideoUrl(rectangle.videoFile || "");
          setResultRectange(rectangle);
          return;
        }
      }
    }
  };

  const handleUpdateAudio = (data) => {
    // Set the audio URL
    const updatedRectangles = rectangles || [];
    if (updatedRectangles[rectangleIndex]) {
      updatedRectangles[rectangleIndex].audioFile = data;
      updatedRectangles[rectangleIndex].videoFile = "";
      updatedRectangles[rectangleIndex].definition = "";
    }
    saveRectanglesToDB(updatedRectangles);
    setAudioUrl(data);
    setVideoUrl("");
  };

  const handleUpdateVideo = (data) => {
    // Set the video URL
    const updatedRectangles = rectangles || [];
    if (updatedRectangles[rectangleIndex]) {
      updatedRectangles[rectangleIndex].videoFile = data;
      updatedRectangles[rectangleIndex].audioFile = "";
      updatedRectangles[rectangleIndex].definition = "";
    }
    saveRectanglesToDB(updatedRectangles);
    setVideoUrl(data);
    setAudioUrl("");
  };

  const handleUpdateDefinition = (data, word) => {
    // Set the definition
    const updatedRectangles = rectangles || [];
    if (updatedRectangles[rectangleIndex]) {
      updatedRectangles[rectangleIndex].definition = data || "";
      updatedRectangles[rectangleIndex].word = word || "";
      updatedRectangles[rectangleIndex].audioFile = "";
      updatedRectangles[rectangleIndex].videoFile = "";
    }
    saveRectanglesToDB(updatedRectangles);
    setAudioUrl("");
    setVideoUrl("");
  };

  const handleClose = () => {
    setVideoModal(false);
    setAudioModal(false);
    // setAllModal(false);
    setDefinitionModal(false);
  };

  const handleCloseModal = () => {
    setAllModal(false);
  };

  const handleMouseOutForCanvas = () => {
    if (isDrawing) {
      setNewRect(null);
      setIsDrawing(false);
      enableScroll();
    }
  };
  const onSelectImage = (url) => {
    onChange({ url: url });
    setpagePoster(url);
  }
  if (!imageUrl && imageUrl === "") {
    return (
      <>
      <MultiFileUploader inModal onSelect={onSelectImage} />
        <FileUploader
          classes={"file-uploaderCstm file-uploader  non-native-file-uploader slide-uploader"}
          handleChange={handleChange}
          name="file"
          types={IMAGE_FILE_TYPE}
        >
          <FileUploadArea>
            {loader && (
              <Progress
                type="line"
                percent={loadingMap}
                size={"small"}
                className={"fileProgress"}
                showInfo={false}
              />
            )}
            <Row className="box box-1">
              <Column span={2}>
                <AddToPhotos color="primary" />
              </Column>
              <Column span={17}>
                <Text>Upload or drop a file right here</Text>
              </Column>
              <Column span={4}>
                <Text>{IMAGE_FILE_TYPE.join(",")}</Text>
              </Column>
            </Row>
          </FileUploadArea>
        </FileUploader>
        <AddImagePlaceHolder className="">+ Add Image</AddImagePlaceHolder>
      </>
    );
  } else {
    return (
      <>
       <MultiFileUploader inModal onSelect={onSelectImage} buttonStyle={{ marginBottom:0 , marginTop:12}} />
        <FileUploader
          classes={"file-uploaderCstm file-uploader  non-native-file-uploader slide-uploader"}
          handleChange={handleChange}
          name="file"
          types={IMAGE_FILE_TYPE}

        >
          <FileUploadArea>
            {loader && (
              <Progress
                type="line"
                percent={loadingMap}
                size={"small"}
                className={"fileProgress"}
                showInfo={false}
              />
            )}
            <Row className="box box-1">
              <Column span={2}>
                <AddToPhotos color="primary" />
              </Column>
              <Column span={17}>
                <Text>Upload or drop a file right here</Text>
              </Column>
              <Column span={4}>
                <Text>{IMAGE_FILE_TYPE.join(",")}</Text>
              </Column>
            </Row>
          </FileUploadArea>
        </FileUploader>
        <Container className="container to_be_print toBePrintAddBook">
          <>
            <Image
              ref={imageRef}
              src={imageUrl}
              alt="To be highlighted"
              className="image to_be_print"
              onLoad={() => {
                const canvas = canvasRef.current;
                const image = imageRef.current;
                setIsImageLoaded(true);
                if (canvas && image) {
                  setImageConfiguration({
                    width: image.naturalWidth,
                    height: image.naturalHeight,
                    clientWidth: image.clientWidth,
                    clientHeight: image.clientHeight,
                  });
                  setNaturalDimensions({
                    width: image.naturalWidth,
                    height: image.naturalHeight,
                  });
                  canvas.width = image.clientWidth;
                  canvas.height = image.clientHeight;
                  redrawCanvas();
                }
              }}
            />

            <CanvasContainer
              ref={canvasRef}
              isPointer={isPointer}
              className="highlight-canvas"
              onMouseOut={handleMouseOutForCanvas}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onClick={handleCanvasClick}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseLeave={handleMouseLeave}
            />
          </>

          {is_image_loaded ? (
            <ControlsContainer>
              <Popover
                content={
                  <SketchPicker
                    color={selectedColor}
                    onChange={handleColorChange}
                  />
                }
                title="Select Color"
                trigger="click"
              >
                <Button
                  style={{
                    width: 50,
                    height: 20,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: selectedColor,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Button>
              </Popover>
              <Dropdown menu={strokeWidthMenu} trigger={["click"]}>
                <Button
                  style={{
                    width: 60,
                    height: 20,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    paddingLeft: 5,
                    paddingRight: 5,
                  }}
                >
                  <LineWidthPreview strokeWidth={strokeWidth} top={0}  color="black" />
                </Button>
              </Dropdown>
              {isHighlighterMode ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span>Opacity:</span>
                  <Slider
                    defaultValue={opacity * 100}
                    onChange={handleOpacityChange}
                    style={{ width: 100 }}
                  />
                </div>
              ) : null}
              <Tooltip title="Undo last highlight">
                <Button icon={<UndoOutlined />} onClick={handleUndo} />
              </Tooltip>
              <Tooltip title="Redo last highlight">
                <Button icon={<RedoOutlined />} onClick={handleRedo} />
              </Tooltip>
              <Tooltip title="Clear All Highlights">
                <Button icon={<DeleteOutlined />} onClick={handleClearAll} />
              </Tooltip>
            </ControlsContainer>
          ) : null}

          <DefaultAudioGenerator
            handleCloseModal={handleCloseModal}
            resultRectangle={resultRectangle}
            handleUpdateAudio={handleUpdateAudio}
            handleUpdateVideo={handleUpdateVideo}
            handleUpdateDefinition={handleUpdateDefinition}
            pageId={pageId}
            setAudioModal={setAudioModal}
            setVideoModal={setVideoModal}
            setDefinitionModal={setDefinitionModal}
            openAudioModal={openAudioModal}
            openVideoModal={openVideoModal}
            openDefinitionModal={openDefinitionModal}
            audioUrl={audioUrl}
            videoUrl={videoUrl}
            openAllModal={openAllModal}
            handleClose={handleClose}
          />
        </Container>
      </>
    );
  }
};

export default SlideHighLighter;

const AddImagePlaceHolder = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid black;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  background-color: #e5e5e5;
`;
